import { mapWithConcurrency, safeTimeout } from '@benzinga/safe-await';
import { Session } from '@benzinga/session';
import type { DelayedQuote, PeerQuote } from '@benzinga/quotes-manager';
import type { ChartDataResponse, ChartManager } from '@benzinga/chart-manager';
import { getRelatedSymbolsForSymbol, RelatedSymbolsForSymbolOptions } from './relatedSymbols';

const fetchChartData = async (chartManager: ChartManager, peerSymbol: string, timeoutMs: number) => {
  const response = await safeTimeout(
    chartManager.getChart({
      from: '1d',
      interval: '2m',
      symbol: peerSymbol,
    }),
    timeoutMs,
  );
  return response?.ok ?? null;
};

const toPeerQuote = (quote: DelayedQuote, chartData: ChartDataResponse | null): PeerQuote => ({
  bzExchange: quote.bzExchange || undefined,
  change: quote.change || undefined,
  changePercent: quote.changePercent || undefined,
  chartCandles: chartData?.candles?.map(candle => candle.close).filter((close): close is number => close != null) ?? [],
  chartInterval: chartData?.interval || undefined,
  companyStandardName: quote.companyStandardName || undefined,
  ethPrice: quote.ethPrice || undefined,
  lastTradePrice: quote.lastTradePrice || undefined,
  symbol: quote.symbol || '',
});

export const getRelatedSymbolPeerQuotes = async (
  session: Session,
  options: RelatedSymbolsForSymbolOptions,
): Promise<Record<string, PeerQuote>> => {
  const relatedSymbols = await getRelatedSymbolsForSymbol(session, options);
  const peerSymbolsToFetch = relatedSymbols.map(relatedSymbol => relatedSymbol.symbol);

  if (peerSymbolsToFetch.length === 0) {
    return {};
  }

  const { QuotesManager } = await import('@benzinga/quotes-manager');
  const { ChartManager } = await import('@benzinga/chart-manager');

  const quotesManager = session.getManager(QuotesManager);
  const chartManager = session.getManager(ChartManager);

  const quotesResponse = await quotesManager.getDelayedQuotes(peerSymbolsToFetch);
  const quotesBySymbol = quotesResponse.ok ?? {};

  const peerCount = peerSymbolsToFetch.length;
  const chartConcurrency = peerCount > 8 ? 4 : 6;
  const chartTimeoutMs = peerCount > 8 ? 5500 : 2000;

  const chartDataResults = await mapWithConcurrency(peerSymbolsToFetch, chartConcurrency, peerSymbol =>
    fetchChartData(chartManager, peerSymbol, chartTimeoutMs),
  );

  const retrySymbols = peerSymbolsToFetch.filter((peerSymbol, index) => {
    const quote = quotesBySymbol[peerSymbol];
    return !!quote && (quote.lastTradePrice != null || quote.ethPrice != null) && chartDataResults[index] == null;
  });

  if (retrySymbols.length > 0) {
    const peerSymbolToIndex = new Map(peerSymbolsToFetch.map((peerSymbol, index) => [peerSymbol, index]));
    const retryResults = await mapWithConcurrency(retrySymbols, 2, peerSymbol =>
      fetchChartData(chartManager, peerSymbol, chartTimeoutMs),
    );

    retrySymbols.forEach((peerSymbol, retryIndex) => {
      const peerIndex = peerSymbolToIndex.get(peerSymbol);
      if (peerIndex !== undefined && retryResults[retryIndex]) {
        chartDataResults[peerIndex] = retryResults[retryIndex];
      }
    });
  }

  return peerSymbolsToFetch.reduce(
    (accumulator, peerSymbol, index) => {
      const quote = quotesBySymbol[peerSymbol];
      if (!quote) return accumulator;

      accumulator[peerSymbol] = toPeerQuote(quote, chartDataResults[index]);
      return accumulator;
    },
    {} as Record<string, PeerQuote>,
  );
};
